/**
 * Tool Registry for managing available tools
 * 
 * This registry maintains all available tools and provides methods
 * to register, retrieve, and execute them.
 */

import { Tool, ToolSchema, ToolImplementation, ToolResult } from './tool-types';

class ToolRegistry {
  private tools: Map<string, Tool> = new Map();

  /**
   * Register a new tool
   */
  register(name: string, schema: ToolSchema, implementation: ToolImplementation): void {
    if (this.tools.has(name)) {
      console.warn(`Tool '${name}' is already registered. Overwriting.`);
    }

    this.tools.set(name, {
      schema,
      implementation
    });

    console.log(`✅ Tool registered: ${name}`);
  }

  /**
   * Unregister a tool
   */
  unregister(name: string): boolean {
    const result = this.tools.delete(name);
    if (result) {
      console.log(`🗑️ Tool unregistered: ${name}`);
    }
    return result;
  }

  /**
   * Get a tool by name
   */
  getTool(name: string): Tool | undefined {
    return this.tools.get(name);
  }

  /**
   * Check if a tool exists
   */
  hasTool(name: string): boolean {
    return this.tools.has(name);
  }

  /**
   * Get all tool schemas (for sending to Realtime API)
   */
  getToolSchemas(): ToolSchema[] {
    return Array.from(this.tools.values()).map(tool => tool.schema);
  }

  /**
   * Get all tool names
   */
  getToolNames(): string[] {
    return Array.from(this.tools.keys());
  }

  /**
   * Execute a tool with the given arguments
   */
  async executeTool(name: string, args: any): Promise<ToolResult> {
    const tool = this.tools.get(name);

    if (!tool) {
      console.error(`❌ Tool '${name}' not found`);
      return {
        success: false,
        error: `Tool '${name}' not found`
      };
    }

    try {
      console.log(`🔧 Executing tool: ${name}`);
      console.log(`📋 Arguments:`, args);

      const result = await tool.implementation(args);

      console.log(`✅ Tool execution completed: ${name}`);
      console.log(`📤 Result:`, result);

      return result;
    } catch (error) {
      console.error(`❌ Tool execution failed: ${name}`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Clear all registered tools
   */
  clear(): void {
    this.tools.clear();
    console.log('🗑️ All tools cleared');
  }

  /**
   * Get the number of registered tools
   */
  get count(): number {
    return this.tools.size;
  }
}

// Export singleton instance
export const toolRegistry = new ToolRegistry();

// Export the class for testing purposes
export { ToolRegistry };

